import React, { useMemo, ReactNode } from 'react';

import { Icon, TranslatedText, withComponent, useRouter, navigate, DrawerProvider, Result } from '@sms/plasma-ui';

import app from '../../config/app.json';
import * as styled from './PageWithMenu.styles';
import { useSecurity } from '@sms/security-plasma-security-auth-sdk-react';
import { Button } from '@sms/plasma-ui/lib/components/Button/Button';
import { ColorPalette, handleTextOverflow, Loader } from '@sms/journal-commons-journal-commons-ui';
import { booksFeatures } from '../../utils/booksFeatures';
import { usePageWithMenu } from './PageWithMenu.hook';

type PageWithMenuProps = {
  children: ReactNode;
  isBooksMenu?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
const SUB_PATH = (window as any).plasma?.environment?.SUB_PATH ?? '/';
const PROCESSED_SUB_PATH = SUB_PATH.replace(/\/$/, '');

function prefixPath(path: string) {
  const treatedPath = path.replace(/^\//, '');
  return `${PROCESSED_SUB_PATH}/${treatedPath}`.replace(/\/\//g, '/');
}

function PageWithMenu(props: PageWithMenuProps) {
  const { children, isBooksMenu } = props;
  const { breadcrumb } = useRouter('breadcrumb');
  const { route } = useRouter('route');
  const permissions = Array.from(useSecurity().permissions);
  const { bookData, bookLoading, hasBookPermission } = usePageWithMenu(isBooksMenu);

  const title = useMemo(() => {
    const defaultSelectedKeys = breadcrumb.map((e) => e.url);
    const bookId = route.params?.bookId ?? "master";

    return (
      <>
        <styled.Menu
          mode="horizontal"
          selectedKeys={defaultSelectedKeys}
          onClick={({ key }) => navigate(key)}
          style={{ fontSize: 16, fontWeight: 500 }}
          id="main-menu"
        >
          {isBooksMenu ? (
            <>
              <styled.MenuItem key={prefixPath(`/${app.code}/books`)}>
                <Icon name="book" prefix="far" />
                <TranslatedText textKey="app.menu.books" />
              </styled.MenuItem>

              <styled.MenuItem key={prefixPath(`/${app.code}/settings`)}>
                <Icon name="cog" prefix="far" />
                <TranslatedText textKey="app.menu.settings" />
              </styled.MenuItem>
            </>
          ) : (
            <>
              <styled.BookMenu key={prefixPath(`/${app.code}/books`)}>
                <Icon prefix="far" name={"chevron-left"} />
                <Icon name={bookData.icon} className="book-icon" prefix="far" />
                <TranslatedText textKey={handleTextOverflow(bookData.name, 50)} />
              </styled.BookMenu>

              {permissions.includes('event.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.EVENTS)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/events`)}>
                  <Icon name="calendar-alt" prefix="far" />
                  <TranslatedText textKey="app.menu.events" />
                </styled.MenuItem>
              )}

              {permissions.includes('task.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.TASKS)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/tasks`)}>
                  <Icon name="tasks" prefix="far" />
                  <TranslatedText textKey="app.menu.tasks" />
                </styled.MenuItem>
              )}

              {permissions.includes('process.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.PROCESSES)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/processes`)}>
                  <Icon name="code-branch" prefix="far" />
                  <TranslatedText textKey="app.menu.processes" />
                </styled.MenuItem>
              )}

              {permissions.includes('decision.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.DECISION_TABLES)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/decisionTables`)}>
                  <Icon name="table" prefix="far" />
                  <TranslatedText textKey="app.menu.decisionTables" />
                </styled.MenuItem>
              )}

              {permissions.includes('rule.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.RULES)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/rules`)}>
                  <Icon name="sitemap" prefix="far" />
                  <TranslatedText textKey="app.menu.rules" />
                </styled.MenuItem>
              )}

              {permissions.includes('report.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.REPORTS)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/reports`)}>
                  <Icon name="paste" prefix="far" />
                  <TranslatedText textKey="app.menu.reports" />
                </styled.MenuItem>
              )}

              {permissions.includes('dashboard.view') && (bookData.features.includes(booksFeatures.ALL) || bookData.features.includes(booksFeatures.BOARDS)) && (
                <styled.MenuItem key={prefixPath(`/${app.code}/book/${bookId}/boards`)}>
                  <Icon name="layer-group" prefix="far" />
                  <TranslatedText textKey="app.menu.boards" />
                </styled.MenuItem>
              )}

              {bookData.settingsPermission && (
                <styled.SettingsMenu key={prefixPath(`/${app.code}/book/edit/${bookId}`)}>
                  <styled.SettingsWrapper>
                    <Button
                      type="primary"
                      title={"app.menu.books.edit"}
                      icon={<Icon name="pen" style={{ color: ColorPalette.PRIMARY }} />}
                      style={{ backgroundColor: ColorPalette.WHITE, color: ColorPalette.PRIMARY }}
                    />
                  </styled.SettingsWrapper>
                </styled.SettingsMenu>
              )}
            </>
          )}
        </styled.Menu>
      </>
    );
  }, [breadcrumb, isBooksMenu, bookData]);

  if (!isBooksMenu) {
    if (bookLoading)
      return <Loader />;

    if (!hasBookPermission)
      return <Result status="403" />;

    if (!bookData.name)
      return <Result status="404" />;
  }

  return (
    <DrawerProvider>
      {title}
      {children}
    </DrawerProvider>
  );
}

export default withComponent(PageWithMenu);
